import { scaleFont } from '../utils/scaleFont';

import { buildDefaultMulti } from './buildDefaultMulti';
import { Direction } from './direction';
import type { Line } from './Line';
import { MathObject } from './MathObject';
import type { Multi, MultiDependencies } from './Multi';
import { Text } from './Text';

const NTH_ROOT_SCALE = 0.7;
const PADDING = 2;

export class Root extends MathObject {
  rad: Multi;
  root: Multi;

  constructor(line: Line, radicand: Multi, root: Multi) {
    super();

    this.t = 'root';
    this.gmmName = 'root';

    this.rad = radicand;
    this.rad.parent = this;

    this.root = root;
    this.root.parent = this;
    this.root.isOptional = true;

    this.line = line;

    this.children.push(this.rad);

    if (this.showRoot()) this.children.push(this.root);

    scaleFont(this.root, (this.root.children[0] as Text).font, NTH_ROOT_SCALE);
  }

  getPaintedCharCount(): number {
    // not currently counting root as a char
    return this.rad.getPaintedCharCount();
  }

  showRoot(): boolean {
    const rootText = this.root.children[0] as Text;
    const rootValue = rootText.text;

    // check if root is part of a student's attempt and therefore editable
    const isEditable = !!this.line?.e;

    // if root is 2 and not editable, don't show it
    return rootValue !== '2' || isEditable;
  }

  setEditable(b: boolean): void {
    this.rad.setEditable(b);
    this.root.setEditable(b);
  }

  setFont(f: string): void {
    this.rad.setFont(f);
    scaleFont(this.root, f, NTH_ROOT_SCALE);
  }

  setRadicandAndRoot(radicand: Multi, root: Multi): void {
    this.children = [];
    this.children.push(radicand);
    if (this.showRoot()) this.children.push(root);
    this.rad = radicand;
    this.root = root;
    radicand.parent = this;
    root.parent = this;
  }

  setLine(line: Line): void {
    this.line = line;
    this.rad.setLine(line);
    this.root.setLine(line);
  }

  toXML(): string {
    return (
      '<r><rad>' +
      this.rad.toXML() +
      '</rad><root>' +
      this.root.toXML() +
      '</root></r>'
    );
  }

  swapString(old: string, replacer: string): void {
    this.rad.swapString(old, replacer);
    this.root.swapString(old, replacer);
  }

  grabFocus(direction: Direction): void {
    this.rad.grabFocus(direction);
  }

  buildSizeRecursive(): void {
    this.rad.buildSizeRecursive();
    this.root.buildSizeRecursive();
    this.buildSize();
  }

  slantWidth(): number {
    return this.rad.viewportH * Math.tan(Math.PI / 6);
  }

  buildSize(): void {
    const h = this.rad.viewportH + this.extraHeight();
    const w = this.rad.viewportW + this.root.viewportW + this.slantWidth();

    this.setAllDim(w, h);
    this.setPositionsOfChildren();
  }

  getVertToBaseline(): number {
    return this.extraHeight() + this.rad.getVertToBaseline();
  }

  extraHeight(): number {
    const heightDiff = this.rad.viewportH / 2 - this.root.viewportH;

    // in megafile, was "ret.showRoot" -- so just existence of function, which was always true
    return this.showRoot() && heightDiff < 0
      ? Math.abs(heightDiff)
      : this.getLineWidth() + PADDING;
  }

  setPositionsOfChildren(): void {
    this.rad.viewportX = this.root.viewportW + this.slantWidth();
    this.rad.viewportY = this.extraHeight();

    this.root.viewportX = PADDING;
    this.root.viewportY =
      this.viewportH / 2 - this.root.viewportH + this.extraHeight();
  }

  childSizeChanged(): void {
    this.buildSize();
    (this.parent as MathObject)?.childSizeChanged();
  }

  paintMe(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.strokeStyle = this.fillStyle || 'black';
    ctx.lineWidth = this.getLineWidth();
    ctx.beginPath();

    const h = this.viewportH;
    const w = this.root.viewportW;

    // over two from original design to give clarity to preceding negative symbol
    ctx.moveTo(PADDING, h / 2 + this.extraHeight());
    ctx.lineTo(w, h / 2 + this.extraHeight());
    ctx.lineTo(w + this.slantWidth() / 4 - 1, h - this.getLineWidth() / 2);
    ctx.lineTo(w + this.slantWidth() - 1, this.getLineWidth() / 2);
    ctx.lineTo(this.viewportW + PADDING, this.getLineWidth() / 2);

    ctx.stroke();
    ctx.restore();
  }

  getLineWidth(): number {
    return this.fillStyle ? 2 : 1;
  }

  add(tile: MathObject, rad: any): void {
    if (rad) this.rad.add(tile);
    else this.root.add(tile);
  }
}

export function buildRoot(
  problemJS: MultiDependencies,
  line: Line,
  radicand?: Multi,
  root?: Multi
): Root {
  return new Root(
    line,
    radicand || buildDefaultMulti(problemJS, line),
    root || buildDefaultMulti(problemJS, line)
  );
}
